/**
 * Variant gallery swaps for PDP / Quick Shop.
 * Uses custom.variant_images (list.file_reference), with wip.variant_images
 * fallback seeded by Liquid — see .shopify/metafields.json.
 *
 * Composition for metafield variants: metafield images first, then product
 * listing image media (URL-deduped). Empty metafield → rebuild default from
 * productMedia (image-only). Products with video/model keep a minimal
 * defaultHTML fallback because the JS image builder cannot recreate those.
 *
 * Prefer in-place slide src updates when slide counts match; otherwise
 * destroy Flickity → replace scoped <product-images> → cacheElements +
 * initProductSlider (+ Photoswipe sync). Never call product.init().
 */
(function () {
  const lastUpdatedByProduct = new Map();
  const DEFAULT_GALLERY_ID = 'default';
  const PREFETCH_IMAGE_LIMIT = 2;
  const HERO_WIDTHS = [360, 540, 720, 900, 1080];
  const NON_IMAGE_MEDIA_SELECTOR =
    '[data-product-media-type-model], [data-video-type], .product__video-wrapper, .product-video-trigger, model-viewer, video';

  function withWidth(url, width) {
    if (!url) return url;
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    }
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  /** Match sizes="(min-width: 769px) 50vw, 100vw" without always fetching 1080. */
  function preferredHeroWidth() {
    const vw = window.innerWidth || 1080;
    const slot = vw < 769 ? vw : Math.ceil(vw / 2);
    for (let i = 0; i < HERO_WIDTHS.length; i++) {
      if (HERO_WIDTHS[i] >= slot) return HERO_WIDTHS[i];
    }
    return 1080;
  }

  /** Stable key for dedupe across width/query variants of the same CDN asset. */
  function normalizeImageKey(url) {
    if (!url) return '';
    try {
      const absolute = url.startsWith('//') ? `https:${url}` : url;
      const parsed = new URL(absolute, window.location.origin);
      return parsed.pathname;
    } catch (_) {
      return String(url)
        .replace(/^https?:/, '')
        .replace(/\?.*$/, '')
        .split('&')[0];
    }
  }

  /**
   * Metafield URLs first, then product listing images; skip duplicates by path.
   */
  function composeGalleryUrls(metafieldUrls, productMediaUrls) {
    const result = [];
    const seen = new Set();

    function pushUnique(url) {
      const key = normalizeImageKey(url);
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(url);
    }

    (metafieldUrls || []).forEach(pushUnique);
    (productMediaUrls || []).forEach(pushUnique);
    return result;
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getGalleryStore(productId) {
    return window.variantGalleryByProduct?.[productId] || null;
  }

  /** Path keys already handed to Image() — skip re-prefetch. */
  const prefetchedImageKeys = new Set();

  function prefetchGalleryUrls(urls) {
    const width = preferredHeroWidth();
    (urls || []).forEach((url) => {
      const key = normalizeImageKey(url);
      if (!key || prefetchedImageKeys.has(key)) return;
      prefetchedImageKeys.add(key);
      const img = new Image();
      img.decoding = 'async';
      img.src = withWidth(url, width);
    });
  }

  function getVariantsObject(product) {
    if (!product) return null;
    if (Array.isArray(product.variantsObject)) return product.variantsObject;
    const jsonEl = product.querySelector('[data-variant-json]');
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent);
    } catch (_) {
      return null;
    }
  }

  /**
   * Resolve variant id for a hovered option without changing selection.
   * Combines currently checked options with the hovered option's value
   * (metal × birthstone → one combined variant).
   */
  function resolveVariantIdForHoveredOption(product, optionInput) {
    if (!product || !optionInput) return null;

    const variants = getVariantsObject(product);
    if (!variants?.length) return null;

    const hoveredIndex = optionInput.dataset.index;
    const hoveredValue = optionInput.value;
    if (!hoveredIndex) return null;

    const selected = {};
    product.querySelectorAll('[data-variant-input]').forEach((el) => {
      const type = el.getAttribute('type');
      if (type === 'radio' || type === 'checkbox') {
        if (el.checked) selected[el.dataset.index] = el.value;
      } else if (el.value != null && el.value !== '') {
        selected[el.dataset.index] = el.value;
      }
    });
    selected[hoveredIndex] = hoveredValue;

    const match = variants.find((variant) =>
      Object.keys(selected).every((key) => variant[key] === selected[key])
    );
    return match?.id ?? null;
  }

  function getGalleryUrlsForVariant(productId, variantId) {
    const store = getGalleryStore(productId);
    if (!store || !variantId) return [];

    const customUrls = store.customData?.[variantId];
    if (!customUrls?.length) return [];

    // Same composition as the gallery swap path.
    return composeGalleryUrls(customUrls, store.productMedia);
  }

  /** Last product:variant prefetched this session — avoid duplicate hover storms. */
  let lastPrefetchKey = null;

  function prefetchVariantImagesForOption(label) {
    if (!label || !label.closest('.variant-wrapper.dropdown-selector')) return;

    // Prefer scoped lookup (Quick Shop + PDP may coexist).
    const optionInput =
      label.closest('.variant-input')?.querySelector('[data-variant-input]') ||
      (label.htmlFor && document.getElementById(label.htmlFor));
    if (!optionInput) return;

    const product = label.closest('product-component');
    if (!product) return;

    const productId = String(
      product.getAttribute('data-product-id') || product.productId || ''
    );
    if (!productId || !getGalleryStore(productId)) return;

    const variantId = resolveVariantIdForHoveredOption(product, optionInput);
    if (!variantId) return;

    const prefetchKey = `${productId}:${variantId}`;
    if (lastPrefetchKey === prefetchKey) return;
    lastPrefetchKey = prefetchKey;

    // Cap: hero + maybe next — not the full metafield + productMedia stack.
    const urls = getGalleryUrlsForVariant(productId, variantId).slice(
      0,
      PREFETCH_IMAGE_LIMIT
    );
    prefetchGalleryUrls(urls);
  }

  // pointerover/focusin bubble (unlike pointerenter) — works for Quick Shop DOM too.
  let prefetchHoverRaf = null;
  let pendingPrefetchLabel = null;

  function scheduleVariantImagePrefetch(label) {
    pendingPrefetchLabel = label;
    if (prefetchHoverRaf) return;
    prefetchHoverRaf = requestAnimationFrame(() => {
      prefetchHoverRaf = null;
      const target = pendingPrefetchLabel;
      pendingPrefetchLabel = null;
      if (target) prefetchVariantImagesForOption(target);
    });
  }

  document.addEventListener('pointerover', function (event) {
    const label = event.target.closest?.(
      '.variant-wrapper.dropdown-selector .variant__button-label'
    );
    if (!label) return;
    scheduleVariantImagePrefetch(label);
  });

  document.addEventListener('focusin', function (event) {
    const label = event.target.closest?.(
      '.variant-wrapper.dropdown-selector .variant__button-label'
    );
    if (!label) return;
    scheduleVariantImagePrefetch(label);
  });

  function getProductComponent(detail) {
    if (!detail) return null;
    const { sectionId, productId } = detail;
    if (sectionId && productId) {
      return (
        document.getElementById(`ProductSection-${sectionId}-${productId}`) ||
        document.querySelector(
          `product-component[data-section-id="${sectionId}"][data-product-id="${productId}"]`
        )
      );
    }
    if (productId) {
      return document.querySelector(`product-component[data-product-id="${productId}"]`);
    }
    return null;
  }

  function ensureVariantImagePatch(product) {
    if (!product || product._ctwfVariantImagePatched) return;
    product._ctwfVariantImagePatched = true;

    const original = product.updateVariantImage?.bind(product);
    if (!original) return;

    product.updateVariantImage = function (evt) {
      const variantId = evt?.detail?.variant?.id;
      const store = getGalleryStore(
        product.productId || product.getAttribute('data-product-id')
      );
      if (variantId && store?.customData?.[variantId]) {
        // Custom metafield gallery owns this variant's media.
        return;
      }

      if (!product.cache?.mainSlider?.isConnected) return;

      try {
        return original(evt);
      } catch (_) {
        // Featured-media thumb may be missing after a gallery swap.
      }
    };
  }

  function destroyProductSlider(product) {
    if (!product) return;

    if (product.flickity && typeof product.flickity.destroy === 'function') {
      try {
        product.flickity.destroy();
      } catch (_) {
        /* detached slider */
      }
    }
    product.flickity = null;

    if (Array.isArray(product.mediaObservers)) {
      product.mediaObservers.forEach((observer) => {
        try {
          observer.disconnect();
        } catch (_) {
          /* noop */
        }
      });
      product.mediaObservers = [];
    }
  }

  function rebindPhotoswipeSync(product) {
    const container = product.container?.querySelector(product.selectors?.imageContainer);
    if (!container || container._ctwfPhotoswipeBound) return;

    container.addEventListener('photoswipe:afterChange', function (evt) {
      if (product.flickity) {
        product.flickity.goToSlide(evt.detail.index);
      }
    });
    container._ctwfPhotoswipeBound = true;
  }

  function rebindProductMedia(product) {
    // product-component may not have finished init() yet (is-land); leave DOM
    // in place and let the upcoming init bind the new nodes.
    if (
      typeof product.cacheElements !== 'function' ||
      typeof product.initProductSlider !== 'function' ||
      !product.selectors
    ) {
      return;
    }

    product.cacheElements();

    const imagesEl = product.container?.querySelector(product.selectors.imageContainer);
    if (imagesEl?.dataset?.mediaGalleryLayout) {
      product.settings = product.settings || {};
      product.settings.mediaGalleryLayout = imagesEl.dataset.mediaGalleryLayout;
    }

    product.settings = product.settings || {};
    product.settings.currentSlideIndex = 0;

    product.initProductSlider();
    rebindPhotoswipeSync(product);

    const productImages = product.container?.querySelector('product-images');
    if (productImages && typeof productImages.initImageZoom === 'function' && !productImages.photoswipe) {
      productImages.initImageZoom();
    }
  }

  function buildGalleryHTML(productId, variantId, imageUrls) {
    const store = getGalleryStore(productId);
    const meta = store?.meta || {};
    const sectionId = meta.sectionId || '';
    const title = escapeHtml(meta.productTitle || '');
    const zoomEnabled = meta.zoomEnable !== false;
    const layout = meta.mediaGalleryLayout || 'below';
    const count = imageUrls.length;
    const heroWidth = preferredHeroWidth();

    const slides = imageUrls
      .map((url, i) => {
        const src = withWidth(url, heroWidth);
        const zoom = withWidth(url, 1800);
        const srcset = HERO_WIDTHS.map((w) => `${withWidth(url, w)} ${w}w`).join(', ');
        const mediaId = `meta-${sectionId}-${variantId}-${i + 1}`;
        const selected = i === 0 ? ' starting-slide is-selected' : '';
        const loading = i === 0 ? 'eager' : 'lazy';

        return `
      <div class="product-main-slide${selected}" data-index="${i}" data-media-id="${mediaId}">
        <div data-product-image-main class="product-image-main" data-size="square">
          <div class="image-wrap" style="height:0; padding-bottom:100%;">
            <image-element data-aos="image-fade-in" data-aos-offset="150" data-image-type="photoswipe">
              <img
                src="${src}"
                alt="${title}"
                srcset="${srcset}"
                width="1080"
                height="1080"
                loading="${loading}"
                class="photoswipe__image image-element"
                data-photoswipe-src="${zoom}"
                data-photoswipe-width="2000"
                data-photoswipe-height="2000"
                data-index="${i + 1}"
                sizes="(min-width: 769px) 50vw, 100vw"
              >
            </image-element>
            <button type="button" class="btn btn--secondary btn--circle js-photoswipe__zoom product__photo-zoom" aria-label="Zoom"></button>
          </div>
        </div>
      </div>`;
      })
      .join('');

    let thumbs = '';
    if (count > 1) {
      const thumbItems = imageUrls
        .map((url, i) => {
          const thumb = withWidth(url, 720);
          const href = withWidth(url, 1800);
          const srcset = [120, 360, 540, 720].map((w) => `${withWidth(url, w)} ${w}w`).join(', ');
          const mediaId = `meta-${sectionId}-${variantId}-${i + 1}`;
          const active = i === 0 ? ' is-active' : '';
          return `
            <div class="product__thumb-item" data-index="${i}">
              <a href="${href}" data-product-thumb class="product__thumb js-no-transition${active}" data-index="${i}" data-id="${mediaId}">
                <div class="image-wrap image-wrap__thumbnail" style="height:0; padding-bottom:100%;">
                  <image-element data-aos="image-fade-in" data-aos-offset="150" class="aos-init aos-animate">
                    <img src="${thumb}" alt="${title}" srcset="${srcset}" width="720" height="720" loading="lazy" class="image-element" sizes="(min-width: 769px) 80px, 100vw">
                  </image-element>
                </div>
              </a>
            </div>`;
        })
        .join('');

      thumbs = `
        <div data-product-thumbs class="product__thumbs product__thumbs--${layout} product__thumbs-placement--left small--hide" data-position="${layout}" data-arrows="true">
          <button type="button" class="product__thumb-arrow product__thumb-arrow--prev hide" aria-label="Previous"></button>
          <div class="product__thumbs--scroller">${thumbItems}</div>
          <button type="button" class="product__thumb-arrow product__thumb-arrow--next" aria-label="Next"></button>
        </div>`;
    }

    return `<product-images
      data-product-images
      data-variant-gallery-id="${variantId}"
      data-zoom="${zoomEnabled}"
      data-has-slideshow="${count > 1 ? 'true' : 'false'}"
      data-media-gallery-layout="${layout}"
    >
      <div class="product__photos product__photos-${sectionId} product__photos--${layout}">
        <div class="product__main-photos" data-product-single-media-group>
          <div data-product-photos data-zoom="${zoomEnabled}" class="product-slideshow" id="ProductPhotos-${sectionId}">
            ${slides}
          </div>
        </div>
        ${thumbs}
      </div>
    </product-images>`;
  }

  function stripModuleScripts(html) {
    return String(html || '').replace(
      /<script\b[^>]*type=["']module["'][^>]*>[\s\S]*?<\/script>/gi,
      ''
    );
  }

  function getActiveGalleryId(product) {
    const el = product.querySelector('product-images');
    const id = el?.dataset?.variantGalleryId;
    return id ? String(id) : null;
  }

  function isShowingDefaultGallery(activeId) {
    return !activeId || activeId === DEFAULT_GALLERY_ID;
  }

  function canUpdateGalleryInPlace(productImagesEl, urls) {
    if (!productImagesEl || !urls?.length) return false;
    if (productImagesEl.querySelector(NON_IMAGE_MEDIA_SELECTOR)) return false;

    const slides = productImagesEl.querySelectorAll('.product-main-slide');
    if (slides.length !== urls.length) return false;

    for (let i = 0; i < slides.length; i++) {
      const img = slides[i].querySelector(
        'img.photoswipe__image, .product-image-main img, img.image-element'
      );
      if (!img) return false;
    }

    const thumbs = productImagesEl.querySelectorAll('.product__thumb-item');
    if (thumbs.length > 0 && thumbs.length !== urls.length) return false;

    return true;
  }

  function applySlideImage(img, url, opts) {
    const heroWidth = opts.width;
    img.src = withWidth(url, heroWidth);
    if (opts.widths) {
      img.srcset = opts.widths.map((w) => `${withWidth(url, w)} ${w}w`).join(', ');
    }
    if (opts.title != null) img.alt = opts.title;
    if (opts.loading) img.loading = opts.loading;
    if (img.hasAttribute('data-photoswipe-src')) {
      img.setAttribute('data-photoswipe-src', withWidth(url, opts.zoom || 1800));
    }
    if (opts.index != null) img.dataset.index = String(opts.index);
  }

  function updateGalleryInPlace(product, productImagesEl, productId, galleryId, urls) {
    const store = getGalleryStore(productId);
    const meta = store?.meta || {};
    const sectionId = meta.sectionId || '';
    const title = meta.productTitle || '';
    const heroWidth = preferredHeroWidth();
    const slides = productImagesEl.querySelectorAll('.product-main-slide');

    productImagesEl.dataset.variantGalleryId = String(galleryId);

    slides.forEach((slide, i) => {
      const url = urls[i];
      const mediaId = `meta-${sectionId}-${galleryId}-${i + 1}`;
      const img = slide.querySelector(
        'img.photoswipe__image, .product-image-main img, img.image-element'
      );

      slide.dataset.mediaId = mediaId;
      slide.dataset.index = String(i);
      slide.classList.toggle('starting-slide', i === 0);
      slide.classList.toggle('is-selected', i === 0);

      applySlideImage(img, url, {
        width: heroWidth,
        widths: HERO_WIDTHS,
        loading: i === 0 ? 'eager' : 'lazy',
        title,
        zoom: 1800,
        index: i + 1,
      });
    });

    const thumbs = productImagesEl.querySelectorAll('.product__thumb-item');
    thumbs.forEach((thumb, i) => {
      const url = urls[i];
      if (!url) return;
      const mediaId = `meta-${sectionId}-${galleryId}-${i + 1}`;
      const link = thumb.querySelector('[data-product-thumb]');
      const img = thumb.querySelector('img');

      thumb.dataset.index = String(i);
      if (link) {
        link.href = withWidth(url, 1800);
        link.dataset.index = String(i);
        link.dataset.id = mediaId;
        link.classList.toggle('is-active', i === 0);
      }
      if (img) {
        applySlideImage(img, url, {
          width: 720,
          widths: [120, 360, 540, 720],
          loading: 'lazy',
          title,
        });
      }
    });

    if (product.settings) {
      product.settings.currentSlideIndex = 0;
    }

    if (product.flickity && typeof product.flickity.goToSlide === 'function') {
      try {
        product.flickity.goToSlide(0);
      } catch (_) {
        /* slider mid-teardown */
      }
    }

    return true;
  }

  function replaceGalleryHTML(product, productImagesEl, newHTML) {
    destroyProductSlider(product);
    productImagesEl.outerHTML = stripModuleScripts(newHTML);
    rebindProductMedia(product);
  }

  function updateProductImages(detail) {
    if (!detail?.variant?.id) return;

    const variantId = detail.variant.id;
    const product = getProductComponent(detail);
    if (!product) return;

    const productId = String(
      detail.productId || product.getAttribute('data-product-id') || product.productId || ''
    );
    const store = getGalleryStore(productId);
    if (!store) return;

    const productKey = productId || product.id;
    if (lastUpdatedByProduct.get(productKey) === variantId) return;

    window.currentVariant = detail.variant;

    const customUrls = store.customData?.[variantId];
    const activeId = getActiveGalleryId(product);

    // Skip when Liquid already rendered this variant's custom gallery.
    if (customUrls?.length && activeId === String(variantId)) {
      lastUpdatedByProduct.set(productKey, variantId);
      ensureVariantImagePatch(product);
      return;
    }

    // Skip when showing default gallery and this variant has no custom images.
    if (!customUrls?.length && isShowingDefaultGallery(activeId)) {
      lastUpdatedByProduct.set(productKey, variantId);
      ensureVariantImagePatch(product);
      return;
    }

    const productImagesEl = product.querySelector('product-images');
    if (!productImagesEl) return;

    ensureVariantImagePatch(product);

    let targetUrls = null;
    let galleryId = null;
    let fallbackHTML = null;

    if (customUrls?.length) {
      targetUrls = composeGalleryUrls(customUrls, store.productMedia);
      galleryId = String(variantId);
    } else if (store.defaultHTML) {
      // Video/model products: JS builder cannot recreate non-image slides.
      fallbackHTML = store.defaultHTML;
      galleryId = DEFAULT_GALLERY_ID;
    } else if (store.productMedia?.length) {
      targetUrls = store.productMedia.slice();
      galleryId = DEFAULT_GALLERY_ID;
    } else {
      return;
    }

    if (targetUrls?.length && canUpdateGalleryInPlace(productImagesEl, targetUrls)) {
      updateGalleryInPlace(product, productImagesEl, productId, galleryId, targetUrls);
      lastUpdatedByProduct.set(productKey, variantId);
      return;
    }

    const newHTML = targetUrls?.length
      ? buildGalleryHTML(productId, galleryId, targetUrls)
      : fallbackHTML;

    if (!newHTML) return;

    replaceGalleryHTML(product, productImagesEl, newHTML);
    lastUpdatedByProduct.set(productKey, variantId);
  }

  // Patch early so theme variantImageChange can't race before first swap.
  document.addEventListener('product-component:loaded', function (event) {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    const product = document.querySelector(
      `product-component[data-section-id="${sectionId}"]`
    );
    if (product) ensureVariantImagePatch(product);
  });

  document.addEventListener('variant:change', function (event) {
    const detail = event.detail;
    if (!detail?.variant) return;
    updateProductImages(detail);
  });

  // Label handler lives in ctwf_pdp.js — only re-bind for Quick Shop.
  document.addEventListener('quickshop:opened', function () {
    if (typeof window.variantChangeLabelChange === 'function') {
      window.variantChangeLabelChange();
    }
  });
})();
